import { useEffect } from 'react';
import {
  acknowledgeGoogleAdsPurchaseConversion,
  claimGoogleAdsPurchaseConversion,
} from '@/api/google-ads';
import { authClient } from '@/auth/client';
import { clientEnv } from '@/env/client';
import { normalizeGoogleAdsEnhancedConversionEmail } from './google-ads-signup-policy';

type GoogleAdsUserData = {
  email: string;
};

type Gtag = (
  ...args:
    | [
        command: 'event',
        eventName: 'conversion',
        params: {
          send_to: string;
          transaction_id: string;
          value: number;
          currency: string;
          new_customer: boolean;
          event_callback?: () => void;
        },
      ]
    | [command: 'js', date: Date]
    | [command: 'config', tagId: string]
    | [command: 'set', target: 'user_data', data: GoogleAdsUserData]
) => void;

type WindowWithGtag = Window & {
  dataLayer?: unknown[];
  gtag?: Gtag;
  googleAdsPurchaseE2EEnabled?: boolean;
  googleAdsPurchaseTagBootstrapped?: boolean;
};

type PurchaseConversion = {
  currency: string;
  enhancedConversionEmail: string;
  newCustomer: boolean;
  onAcknowledged: () => void;
  transactionId: string;
  value: number;
};

type ConversionDispatch = {
  cancel: () => void;
  started: boolean;
};

const PURCHASE_CONVERSION_SEND_DELAYS_MS = [0, 1500, 5000] as const;
const CLAIM_FAILURE_RETRY_MS = 30 * 1000;
const MISSING_CONVERSION_RETRY_MS = 30 * 1000;
const PURCHASE_RETRY_EVENT = 'google-ads-purchase-retry';

export function GoogleAdsPurchaseTracker() {
  const { data: session, isPending } = authClient.useSession();
  const userId = session?.user?.id;

  useEffect(() => {
    if (!isGoogleAdsPurchaseRuntimeEnabled() || isPending || !userId) return;

    let stopped = false;
    let running = false;
    let retryHandle: number | null = null;
    let cancelDispatch: (() => void) | null = null;

    const cancelRetry = () => {
      if (retryHandle !== null) window.clearTimeout(retryHandle);
      retryHandle = null;
    };
    const scheduleRetry = (delayMs: number) => {
      if (stopped) return;
      cancelRetry();
      retryHandle = window.setTimeout(runAttempt, Math.max(delayMs, 1000));
    };
    const stopTracking = () => {
      stopped = true;
      cancelRetry();
      cancelDispatch?.();
    };
    const acknowledge = async (attemptId: string) => {
      try {
        const result = await acknowledgeGoogleAdsPurchaseConversion({
          data: { attemptId },
        });
        if (!isAcknowledgementResult(result) || !result.acknowledged) {
          scheduleRetry(CLAIM_FAILURE_RETRY_MS);
          return;
        }

        cancelDispatch?.();
        cancelDispatch = null;
        scheduleRetry(1000);
      } catch {
        scheduleRetry(CLAIM_FAILURE_RETRY_MS);
      }
    };
    async function runAttempt() {
      if (stopped || running) return;
      running = true;
      try {
        const claim = await claimGoogleAdsPurchaseConversion();
        if (!isPurchaseClaimResult(claim)) {
          scheduleRetry(CLAIM_FAILURE_RETRY_MS);
          return;
        }
        if (!claim.shouldTrack) {
          if (claim.status === 'pending' && claim.retryAfterMs) {
            scheduleRetry(claim.retryAfterMs);
          } else if (claim.status === 'missing') {
            scheduleRetry(MISSING_CONVERSION_RETRY_MS);
          } else {
            scheduleRetry(1000);
          }
          return;
        }

        cancelDispatch?.();
        const dispatch = dispatchGoogleAdsPurchaseConversion({
          transactionId: claim.transactionId,
          enhancedConversionEmail: claim.enhancedConversionEmail,
          value: claim.value,
          currency: claim.currency,
          newCustomer: claim.newCustomer,
          onAcknowledged: () => {
            void acknowledge(claim.attemptId);
          },
        });
        cancelDispatch = dispatch.cancel;
        scheduleRetry(
          dispatch.started ? claim.retryAfterMs : CLAIM_FAILURE_RETRY_MS
        );
      } catch {
        scheduleRetry(CLAIM_FAILURE_RETRY_MS);
      } finally {
        running = false;
      }
    }
    const retryWhenActive = () => {
      if (document.visibilityState === 'visible') scheduleRetry(1000);
    };

    window.addEventListener('focus', retryWhenActive);
    window.addEventListener('online', retryWhenActive);
    window.addEventListener(PURCHASE_RETRY_EVENT, retryWhenActive);
    document.addEventListener('visibilitychange', retryWhenActive);
    void runAttempt();

    return () => {
      stopTracking();
      window.removeEventListener('focus', retryWhenActive);
      window.removeEventListener('online', retryWhenActive);
      window.removeEventListener(PURCHASE_RETRY_EVENT, retryWhenActive);
      document.removeEventListener('visibilitychange', retryWhenActive);
    };
  }, [isPending, userId]);

  return null;
}

export function requestGoogleAdsPurchaseRetry() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(PURCHASE_RETRY_EVENT));
}

function dispatchGoogleAdsPurchaseConversion({
  currency,
  enhancedConversionEmail,
  newCustomer,
  onAcknowledged,
  transactionId,
  value,
}: PurchaseConversion): ConversionDispatch {
  if (!isGoogleAdsPurchaseRuntimeEnabled() || value <= 0) {
    return { cancel: () => {}, started: false };
  }

  const adsId = clientEnv.VITE_GOOGLE_ADS_ID;
  const label = clientEnv.VITE_GOOGLE_ADS_PURCHASE_CONVERSION_LABEL;
  if (!adsId || !label || !transactionId) {
    return { cancel: () => {}, started: false };
  }

  try {
    const gtagWindow = window as WindowWithGtag;
    ensureGtagQueue(gtagWindow);
    ensureGoogleAdsTagLoaded(gtagWindow, adsId);

    const email = normalizeGoogleAdsEnhancedConversionEmail(
      enhancedConversionEmail
    );
    if (email) {
      gtagWindow.gtag?.('set', 'user_data', { email });
    }

    let acknowledged = false;
    const retryHandles = new Set<number>();
    const cancel = () => {
      for (const handle of retryHandles) window.clearTimeout(handle);
      retryHandles.clear();
    };
    const acknowledgeOnce = () => {
      if (acknowledged) return;
      acknowledged = true;
      cancel();
      onAcknowledged();
    };
    const sendConversion = () => {
      gtagWindow.gtag?.('event', 'conversion', {
        send_to: `${adsId}/${label}`,
        transaction_id: transactionId,
        value: Number.isFinite(value) ? Math.max(0, value) : 0,
        currency: currency.trim().toUpperCase(),
        new_customer: newCustomer,
        event_callback: acknowledgeOnce,
      });
    };

    sendConversion();
    for (const delay of PURCHASE_CONVERSION_SEND_DELAYS_MS.slice(1)) {
      const handle = window.setTimeout(() => {
        retryHandles.delete(handle);
        if (!acknowledged) sendConversion();
      }, delay);
      retryHandles.add(handle);
    }

    return { cancel, started: true };
  } catch {
    return { cancel: () => {}, started: false };
  }
}

function isPurchaseClaimResult(
  value: unknown
): value is Awaited<ReturnType<typeof claimGoogleAdsPurchaseConversion>> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'shouldTrack' in value &&
    'status' in value
  );
}

function isAcknowledgementResult(
  value: unknown
): value is { acknowledged: boolean } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'acknowledged' in value &&
    typeof value.acknowledged === 'boolean'
  );
}

function isGoogleAdsPurchaseRuntimeEnabled() {
  if (import.meta.env.PROD) return true;
  if (import.meta.env.MODE !== 'e2e' || typeof window === 'undefined') {
    return false;
  }
  return (window as WindowWithGtag).googleAdsPurchaseE2EEnabled === true;
}

function ensureGtagQueue(gtagWindow: WindowWithGtag) {
  gtagWindow.dataLayer = gtagWindow.dataLayer || [];
  if (typeof gtagWindow.gtag === 'function') return;

  gtagWindow.gtag = ((...args: Parameters<Gtag>) => {
    gtagWindow.dataLayer?.push(args);
  }) as Gtag;
}

function ensureGoogleAdsTagLoaded(gtagWindow: WindowWithGtag, adsId: string) {
  if (!document.getElementById('google-tag-manager')) {
    const script = document.createElement('script');
    script.id = 'google-tag-manager';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      adsId
    )}`;
    script.async = true;
    script.addEventListener(
      'error',
      () => {
        script.remove();
        gtagWindow.googleAdsPurchaseTagBootstrapped = false;
      },
      { once: true }
    );
    document.head.appendChild(script);
  }

  if (gtagWindow.googleAdsPurchaseTagBootstrapped) return;

  gtagWindow.gtag?.('js', new Date());
  gtagWindow.gtag?.('config', adsId);
  gtagWindow.googleAdsPurchaseTagBootstrapped = true;
}
